/**
 * The HTTP client.
 *
 * §20.2 routes `/api/*` to Netlify Functions in Cloud Mode, and the local
 * Fastify server serves the same paths in Local Event Mode (§18.4). The browser
 * applications call the same URLs either way.
 */

import type { EventPayload } from "@tq/protocol";

export interface ApiError {
  code: string;
  message: string;
  details?: unknown;
}

export class ApiRequestError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details: unknown;

  constructor(status: number, error: ApiError) {
    super(error.message);
    this.name = "ApiRequestError";
    this.code = error.code;
    this.status = status;
    this.details = error.details ?? null;
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`/api${path}`, {
    ...init,
    // The facilitator session is an httpOnly cookie. Without this the cookie is
    // not sent and every console call fails in a way that looks like a server
    // bug rather than a missing credential.
    credentials: "same-origin",
    headers: {
      ...(init.body && !(init.body instanceof FormData)
        ? { "content-type": "application/json" }
        : {}),
      ...authHeaders(),
      ...init.headers,
    },
  });

  if (!response.ok) {
    let error: ApiError = { code: "INTERNAL", message: `Request failed (${response.status}).` };
    try {
      const body = (await response.json()) as { error?: ApiError };
      if (body.error) error = body.error;
    } catch {
      // A non-JSON error body — keep the generic message.
    }
    throw new ApiRequestError(response.status, error);
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

/**
 * The seat credential. Still a header, and still needed: it identifies which
 * player a phone is, which a session cookie does not, and a seat a facilitator
 * vouched for has no session behind it at all.
 *
 * The facilitator key that used to live beside it is gone. It was one shared
 * secret, identical for everyone, held in sessionStorage and never expiring,
 * and it identified nobody — every facilitator action in the audit log was
 * attributed to the same anonymous string. The console now sends a session
 * cookie, and the key survives only as a one-time enrollment token typed into
 * the console once.
 */
const PLAYER_TOKEN_STORAGE = "tq.profileToken";

function authHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  try {
    const playerToken = localStorage.getItem(PLAYER_TOKEN_STORAGE);
    if (playerToken) headers["x-player-token"] = playerToken;
  } catch {
    // Storage unavailable; the request proceeds unauthenticated and the server
    // decides what that is allowed to reach.
  }
  return headers;
}

export function getPlayerToken(): string | null {
  try {
    return localStorage.getItem(PLAYER_TOKEN_STORAGE);
  } catch {
    return null;
  }
}

export function setPlayerToken(token: string | null): void {
  try {
    if (token) localStorage.setItem(PLAYER_TOKEN_STORAGE, token);
    else localStorage.removeItem(PLAYER_TOKEN_STORAGE);
  } catch {
    /* ignored */
  }
}

// ─── Shared shapes ───────────────────────────────────────────────────────────

export interface SessionInvite {
  id: string;
  email: string;
  displayName: string | null;
  status: "joined" | "sent" | "failed" | "revoked" | "pending";
  sendCount: number;
  lastSentAt: string | null;
  lastRedeemedAt: string | null;
  lastError: string | null;
}

export interface InviteOutcome {
  invited: SessionInvite[];
  alreadyInvited: string[];
  invalid: string[];
}

export interface RedeemedInvite {
  /** "seated": this invite already has a seat, and `profileToken` returns to it. */
  status: "seated" | "onboarding";
  profileToken: string | null;
  email: string;
  suggestedName: string | null;
  sessionCode: string;
  sessionTitle: string;
  sessionStatus: string;
}

/** One entry in the console's "Waiting for access" list. */
export interface AccessRequest {
  id: string;
  email: string;
  displayName: string;
  status: "pending" | "approved" | "denied";
  requestedAt: string;
  decidedAt: string | null;
  /** The approved browser has collected its sign-in; the decision is final. */
  signedIn: boolean;
  /** This address could open the console, so it cannot be approved. */
  consoleAccess: boolean;
}

export type AccessRequestState =
  | { status: "pending" | "denied" }
  | {
      status: "approved";
      email: string;
      suggestedName: string;
      sessionCode: string;
      sessionTitle: string;
    };

export interface SessionSnapshot {
  sessionId: string;
  tenantId: string;
  title: string;
  sessionCode: string;
  status: "Draft" | "Lobby" | "Active" | "Paused" | "Completed" | "Cancelled";
  deploymentProfile: "cloud" | "local_event";
  joinUrl: string;
  eventHashtag: string | null;
  /** §35.6 — the three-line brand lockup every surface draws. */
  brand: { top: string; main: string; accent: string };
  board: EventPayload<"board.updated">["board"] | null;
  /**
   * `verified` is present only for the facilitator audience: §31.2.9 keeps the
   * public payload free of markers that single one player out from another.
   */
  players: Array<EventPayload<"session.lobby.updated">["players"][number] & { verified?: boolean }>;
  /** "vouched" means this deployment cannot send email, so nothing is proven. */
  identityMode?: "verified" | "vouched";
  connectedCount: number;
  /** Present when a round is live, so a late connection can render it. */
  currentRound: (EventPayload<"question.opened"> & { status: string }) | null;
  /**
   * §13.2 — the final standings, present once the session is Completed.
   *
   * `session.completed` announces them once. A display that reloads, or opens
   * after the game ended, needs them from somewhere that persists, or it has a
   * finished session and no finish to show.
   */
  finalStandings?: EventPayload<"session.completed">["finalStandings"] | null;
  completedAt?: number | null;
  settings: {
    defaultTimeLimitSeconds: number;
    readingWindowSeconds: number;
    questionResultsSeconds: number;
    overallStandingsSeconds: number;
    aiGradingEnabled: boolean;
    answerReviewEnabled: boolean;
    allowLateJoin: boolean;
    /** F-073 — trims photos, audio, live counters and results detail. */
    lowBandwidthMode: boolean;
    /** §12.5 — the beats wait for the facilitator instead of for a clock. */
    manualBeatAdvance: boolean;
    /** §5.1 — whether the projector may make any sound. */
    soundEnabled: boolean;
    /** Players whose code does not arrive may ask the facilitator instead. */
    accessRequestsEnabled: boolean;
  };
  /** §21.5 — how this client should connect for realtime. */
  realtime: { transport: "local_ws" | "ably"; url: string; channels: string[]; clientId: string };
  /** Server time, so the client can correct its own clock (§21.7, §24.4). */
  serverNow: number;
}

export interface GameVersionSummary {
  id: string;
  title: string;
  versionNumber: number;
  publishedAt: string | null;
  archivedAt: string | null;
  categoryCount: number;
  questionCount: number;
  contentHash: string;
}

export interface SessionSummary {
  id: string;
  title: string;
  sessionCode: string;
  status: string;
  startedAt: string | null;
  endedAt: string | null;
}

export interface ValidationMessageDto {
  severity: "error" | "warning";
  code: string;
  label: string;
  message: string;
  fix: string;
  value?: string | number | null;
  location: { sheet?: string; row?: number; column?: string; pointer?: string };
}

export interface ImportResultDto {
  importJobId: string;
  canPublish: boolean;
  errors: ValidationMessageDto[];
  warnings: ValidationMessageDto[];
  gameVersion:
    | (GameVersionSummary & {
        boardValuePrefix: string;
        categories: Array<{
          id: string;
          name: string;
          displayOrder: number;
          questions: Array<{
            id: string;
            cellOrder: number;
            cellValue: number;
            prompt: string;
            answerMode: string;
            correctAnswer: string;
            acceptedAnswers: string[];
            requiredKeywords: string[];
            forbiddenKeywords: string[];
            explanation: string | null;
            timeLimitSeconds: number | null;
            choices: Array<{
              id: string;
              choiceNumber: number;
              choiceText: string;
              isCorrect: boolean;
            }>;
          }>;
        }>;
      })
    | null;
}

/**
 * One question, with the whole §14 grading rubric the author imported — not
 * only the fields the board preview needs, because the detail page exists to
 * show what a summary leaves out.
 */
export interface QuestionDetail {
  id: string;
  cellOrder: number;
  cellValue: number;
  prompt: string;
  answerMode: string;
  correctAnswer: string;
  acceptedAnswers: string[];
  requiredKeywords: string[];
  forbiddenKeywords: string[];
  explanation: string | null;
  optionalConcepts: string[];
  acceptedTerminology: string[];
  acceptableAbbreviations: Record<string, string>;
  correctExamples: string[];
  partialExamples: string[];
  incorrectExamples: string[];
  knownMisconceptions: string[];
  completeAnswerDescription: string | null;
  timeLimitSeconds: number | null;
  active: boolean;
  choices: Array<{ id: string; choiceNumber: number; choiceText: string; isCorrect: boolean }>;
}

// ─── The new-game wizard ─────────────────────────────────────────────────────

/** Mirrors `DraftQuestionView` in @tq/application. */
export interface DraftQuestion {
  categoryName: string;
  /** How the review screen addresses one question. Stable for the draft's life. */
  questionKey: string;
  cellOrder: number;
  cellValue: number;
  prompt: string;
  answerMode: string;
  correctAnswer: string;
  acceptedAnswers: string[];
  requiredKeywords: string[];
  forbiddenKeywords: string[];
  optionalConcepts: string[];
  acceptedTerminology: string[];
  acceptableAbbreviations: Record<string, string>;
  correctExamples: string[];
  partialExamples: string[];
  incorrectExamples: string[];
  knownMisconceptions: string[];
  completeAnswerDescription: string | null;
  explanation: string | null;
  timeLimitSeconds: number | null;
  active: boolean;
  choices: Array<{ choiceNumber: number; text: string; isCorrect: boolean }>;
}

export interface GameDraft {
  id: string;
  title: string;
  stage: "awaiting_questions" | "awaiting_context" | "in_review" | "approved";
  categories: Array<{ name: string; displayOrder: number; questions: DraftQuestion[] }> | null;
  context: { fileName: string; format: string; characters: number; truncated: boolean } | null;
  enrichment: {
    questionsProcessed: number;
    choicesWritten: number;
    failed: number;
    costMicros: number;
    provider: string | null;
    model: string | null;
    ranAt: string;
  } | null;
  gameVersionId: string | null;
  /** False when no AI provider is configured, so the step can say so up front. */
  enrichmentAvailable: boolean;
  boardRules: { minCategories: number; maxCategories: number; questionsPerCategory: number };
}

/**
 * The spreadsheet step's answer.
 *
 * `passed: false` arrives as a 200, not an error: a spreadsheet with three
 * faults needs all three shown at once with their rows, and an error envelope
 * carries one message. See the header of `wizard-routes.ts`.
 */
export interface AttachQuestionsResponse {
  passed: boolean;
  draft: GameDraft | null;
  errors: ValidationMessageDto[];
  warnings: ValidationMessageDto[];
}

export interface ApproveDraftResponse {
  published: boolean;
  gameVersion: GameVersionSummary | null;
  errors: ValidationMessageDto[];
  warnings: ValidationMessageDto[];
}

/** The subset of a question the review screen may change. */
export interface DraftQuestionEdit {
  prompt?: string;
  correctAnswer?: string;
  acceptedAnswers?: string[];
  requiredKeywords?: string[];
  forbiddenKeywords?: string[];
  acceptedTerminology?: string[];
  optionalConcepts?: string[];
  correctExamples?: string[];
  incorrectExamples?: string[];
  explanation?: string | null;
  completeAnswerDescription?: string | null;
  choices?: Array<{ text: string; isCorrect: boolean }>;
  timeLimitSeconds?: number | null;
  active?: boolean;
}

export interface GameVersionDetail extends GameVersionSummary {
  schemaVersion: string;
  boardValuePrefix: string;
  defaultTimeLimitSeconds: number | null;
  readingWindowSeconds: number | null;
  resultsDisplaySeconds: number | null;
  categories: Array<{
    id: string;
    name: string;
    displayOrder: number;
    questions: QuestionDetail[];
  }>;
}

/**
 * §1, §13 — one Answer Map entry.
 *
 * `source` is the provenance that matters when reading this: `author` came from
 * the import, `generated` was predicted before the question was ever played,
 * `ai` and `fuzzy` were decided during a game, and `human` was ruled by a
 * facilitator. Timestamps are epoch milliseconds.
 */
export interface AnswerMapEntryDto {
  normalizedAnswer: string;
  examples: string[];
  opinion: "correct" | "partial" | "incorrect";
  source: "author" | "human" | "ai" | "fuzzy" | "generated";
  confidence: number | null;
  reason: string | null;
  matchedAnswer: string | null;
  fuzzySimilarity: number | null;
  timesSeen: number;
  firstSeenAt: number;
  lastSeenAt: number;
  humanReviewed: boolean;
  overrideCount: number;
}

export interface AnswerMapRevisionDto {
  normalizedAnswer: string;
  previousOpinion: string | null;
  previousSource: string | null;
  newOpinion: string;
  newSource: string;
  changedByActorType: string;
  changedAt: number;
  reason: string | null;
}

export interface AnswerMapDto {
  entries: AnswerMapEntryDto[];
  revisions: AnswerMapRevisionDto[];
}

// ─── Player ──────────────────────────────────────────────────────────────────

/**
 * §14.5 — how the room is doing, as opposed to how the plumbing is doing.
 *
 * Mirrors `ExperienceSnapshot` in `@tq/application`. Durations are milliseconds
 * and are null until enough samples exist to be meaningful.
 */
export interface Distribution {
  count: number;
  medianMs: number | null;
  p95Ms: number | null;
}

export interface PlayerExperience {
  join: {
    opened: number;
    entered: number;
    seated: number;
    stuck: number;
    duration: Distribution;
  };
  round: {
    answered: number;
    timeToAnswer: Distribution;
    submitLatency: Distribution;
    late: number;
    noAnswer: number;
    droppedDuringQuestion: number;
  };
  slowestActivity: { label: string; medianMs: number } | null;
}

export const api = {
  /** §15.1 — resolves whether this device already has a profile. */
  join(sessionCode: string): Promise<
    | {
        status: "onboarding_required";
        sessionTitle: string;
        sessionCode: string;
        /** Whether the code screen may offer to ask the facilitator instead. */
        accessRequests?: boolean;
      }
    | {
        status: "joined";
        snapshot: SessionSnapshot;
        sessionPlayerId: string;
        displayName: string;
        photoUrl: string | null;
      }
  > {
    return request("/join", {
      method: "POST",
      body: JSON.stringify({ sessionCode, profileToken: getPlayerToken() }),
    });
  },

  /** §36.1 — onboarding. Consent is captured before any photo is requested. */
  createProfile(input: {
    sessionCode: string;
    fullName: string;
    email: string;
    organization: string | null;
    avatarConsent: boolean;
    photo: string | null;
    animalShuffleIndex?: number;
    /** §36.5 — the animal the player picked, by name from `avatarOptions()`. */
    avatarAnimal?: string | null;
  }): Promise<{
    profileToken: string;
    snapshot: SessionSnapshot;
    sessionPlayerId: string;
    displayName: string;
    photoUrl: string | null;
    fullName: string;
    email: string | null;
  }> {
    return request("/profile", { method: "POST", body: JSON.stringify(input) });
  },

  /**
   * §36.1 — change your own name or avatar, in the lobby.
   *
   * There is no `email` here, and there is none on the server either. The
   * address is proved, never asserted, and this call carries a profile token
   * rather than a proven sign-in.
   */
  updateProfile(input: {
    sessionId: string;
    fullName?: string;
    avatarConsent?: boolean;
    photo?: string | null;
    /** §36.5 — the animal the player picked, by name from `avatarOptions()`. */
    avatarAnimal?: string | null;
  }): Promise<{ displayName: string; photoUrl: string | null }> {
    return request("/profile", { method: "PATCH", body: JSON.stringify(input) });
  },

  /**
   * §36.5 — the animal roster this tenant draws from, with each one's glyph.
   *
   * Asked for rather than assumed: the roster is tenant configuration, and a
   * picker with its own private copy is exactly how a player came to choose a
   * llama and be issued a pelican.
   */
  avatarOptions(): Promise<{ animals: Array<{ name: string; emoji: string }> }> {
    return request("/avatar-options");
  },

  /** §26.1 — everything needed to restore after a reconnection. */
  playerState(sessionId: string): Promise<{
    snapshot: SessionSnapshot;
    sessionPlayerId: string;
    displayName: string;
    photoUrl: string | null;
    fullName: string;
    email: string | null;
    totalScore: number;
    position: number | null;
    participants: number;
    hasSubmittedCurrentRound: boolean;
    roundToken: string | null;
  }> {
    return request(`/player-state?sessionId=${encodeURIComponent(sessionId)}`);
  },

  submitAnswer(input: {
    roundToken: string;
    text: string | null;
    choiceId: string | null;
    clientSubmittedAt: number | null;
    firstInputAt: number | null;
    revealedChoicesAt: number | null;
    editCount: number;
    autoSubmitted: boolean;
    idempotencyKey: string;
  }): Promise<{ accepted: boolean; receivedAt: number; alreadyAnswered: boolean }> {
    return request("/answer", { method: "POST", body: JSON.stringify(input) });
  },

  /** §9.3 — revealing choices is server-recorded, and forfeits the typed bonus. */
  revealChoices(roundToken: string): Promise<{
    choices: Array<{ id: string; choiceNumber: number; choiceText: string }>;
  }> {
    return request("/reveal-choices", { method: "POST", body: JSON.stringify({ roundToken }) });
  },

  /**
   * An emailed invitation link, opened. Signs this browser in as the invited
   * address (the cookie arrives with the response) and says what comes next.
   */
  redeemInvite(token: string): Promise<RedeemedInvite> {
    return request("/invites/redeem", { method: "POST", body: JSON.stringify({ token }) });
  },

  /**
   * Ask the facilitator to let this browser in without a code. The token that
   * comes back is the only way to collect the answer; keep it.
   */
  requestAccess(input: {
    sessionCode: string;
    email: string;
    name: string;
  }): Promise<{ token: string; status: "pending" }> {
    return request("/access-requests", { method: "POST", body: JSON.stringify(input) });
  },

  /** Where the request stands. "approved" means signed in: the cookie came with it. */
  accessRequestStatus(token: string): Promise<AccessRequestState> {
    return request("/access-requests/status", { method: "POST", body: JSON.stringify({ token }) });
  },

  // ─── Facilitator ───────────────────────────────────────────────────────────

  listAccessRequests(sessionId: string): Promise<{ requests: AccessRequest[] }> {
    return request(`/sessions/${encodeURIComponent(sessionId)}/access-requests`);
  },

  decideAccessRequests(
    sessionId: string,
    requestIds: string[],
    decision: "approve" | "deny",
  ): Promise<{ changed: number; refused: string[]; requests: AccessRequest[] }> {
    return request(`/sessions/${encodeURIComponent(sessionId)}/access-requests/decide`, {
      method: "POST",
      body: JSON.stringify({ requestIds, decision }),
    });
  },

  listInvites(sessionId: string): Promise<{ invites: SessionInvite[] }> {
    return request(`/sessions/${encodeURIComponent(sessionId)}/invites`);
  },

  /** The server re-parses `list`; canonizing it first is for the facilitator's eyes. */
  sendInvites(sessionId: string, list: string): Promise<InviteOutcome> {
    return request(`/sessions/${encodeURIComponent(sessionId)}/invites`, {
      method: "POST",
      body: JSON.stringify({ list }),
    });
  },

  /** A new link; the previous one stops working. Also undoes a revoke. */
  resendInvite(sessionId: string, inviteId: string): Promise<{ invite: SessionInvite }> {
    return request(
      `/sessions/${encodeURIComponent(sessionId)}/invites/${encodeURIComponent(inviteId)}/resend`,
      { method: "POST" },
    );
  },

  revokeInvite(sessionId: string, inviteId: string): Promise<{ invite: SessionInvite }> {
    return request(
      `/sessions/${encodeURIComponent(sessionId)}/invites/${encodeURIComponent(inviteId)}/revoke`,
      { method: "POST" },
    );
  },

  /**
   * design §4 — seat someone whose code never arrived. The seat is unverified
   * and says so; the audit records which facilitator vouched.
   */
  seatPlayer(input: {
    sessionCode: string;
    fullName: string;
    /** Optional and unverified: where the player's report is sent. */
    email?: string;
    organization?: string | null;
  }): Promise<{
    sessionPlayerId: string;
    displayName: string;
    verified: boolean;
    joinUrl: string;
  }> {
    return request("/seat-player", { method: "POST", body: JSON.stringify(input) });
  },

  listGames(): Promise<{ versions: GameVersionSummary[] }> {
    return request("/games");
  },

  /** §14.1 — one version with its questions and full grading rubric. */
  getGame(gameVersionId: string): Promise<{ gameVersion: GameVersionDetail }> {
    return request(`/games/${encodeURIComponent(gameVersionId)}`);
  },

  /** §1, §13 — what this question has learned, and every correction to it. */
  getAnswerMap(questionId: string): Promise<AnswerMapDto> {
    return request(`/answer-map?questionId=${encodeURIComponent(questionId)}`);
  },

  importGame(fileName: string, data: string): Promise<ImportResultDto> {
    return request("/games/import", { method: "POST", body: JSON.stringify({ fileName, data }) });
  },

  publishGame(gameVersionId: string): Promise<{ gameVersion: GameVersionSummary }> {
    return request(`/games/publish`, { method: "POST", body: JSON.stringify({ gameVersionId }) });
  },

  archiveGame(gameVersionId: string): Promise<{ gameVersion: GameVersionSummary }> {
    return request(`/games/archive`, { method: "POST", body: JSON.stringify({ gameVersionId }) });
  },

  // ─── The new-game wizard ───────────────────────────────────────────────────

  startDraft(title: string): Promise<{ draft: GameDraft }> {
    return request("/games/wizard/start", { method: "POST", body: JSON.stringify({ title }) });
  },

  listDrafts(): Promise<{ drafts: GameDraft[] }> {
    return request("/games/wizard");
  },

  getDraft(draftId: string): Promise<{ draft: GameDraft }> {
    return request(`/games/wizard/${encodeURIComponent(draftId)}`);
  },

  discardDraft(draftId: string): Promise<{ discarded: boolean }> {
    return request(`/games/wizard/${encodeURIComponent(draftId)}`, { method: "DELETE" });
  },

  attachDraftQuestions(
    draftId: string,
    fileName: string,
    data: string,
  ): Promise<AttachQuestionsResponse> {
    return request(`/games/wizard/${encodeURIComponent(draftId)}/questions`, {
      method: "POST",
      body: JSON.stringify({ fileName, data }),
    });
  },

  attachDraftContext(
    draftId: string,
    fileName: string,
    data: string,
  ): Promise<{ draft: GameDraft }> {
    return request(`/games/wizard/${encodeURIComponent(draftId)}/context`, {
      method: "POST",
      body: JSON.stringify({ fileName, data }),
    });
  },

  clearDraftContext(draftId: string): Promise<{ draft: GameDraft }> {
    return request(`/games/wizard/${encodeURIComponent(draftId)}/context`, { method: "DELETE" });
  },

  /** One provider call per question, in sequence — this is a slow request. */
  enrichDraft(draftId: string): Promise<{ draft: GameDraft; summary: GameDraft["enrichment"] }> {
    return request(`/games/wizard/${encodeURIComponent(draftId)}/enrich`, { method: "POST" });
  },

  editDraftQuestion(
    draftId: string,
    questionKey: string,
    edit: DraftQuestionEdit,
  ): Promise<{ draft: GameDraft }> {
    return request(`/games/wizard/${encodeURIComponent(draftId)}/question`, {
      method: "PATCH",
      body: JSON.stringify({ questionKey, edit }),
    });
  },

  approveDraft(draftId: string): Promise<ApproveDraftResponse> {
    return request(`/games/wizard/${encodeURIComponent(draftId)}/approve`, { method: "POST" });
  },

  createSession(input: {
    gameVersionId: string;
    title: string;
    sessionCode: string | null;
    deploymentProfile: "cloud" | "local_event";
    defaultTimeLimitSeconds: number | null;
    readingWindowSeconds: number | null;
    questionResultsSeconds: number | null;
    overallStandingsSeconds: number | null;
    aiGradingEnabled: boolean;
    answerReviewEnabled: boolean;
    allowLateJoin: boolean;
    lowBandwidthMode: boolean;
    manualBeatAdvance: boolean;
    soundEnabled: boolean;
    accessRequestsEnabled: boolean;
  }): Promise<{ snapshot: SessionSnapshot }> {
    return request("/sessions", { method: "POST", body: JSON.stringify(input) });
  },

  listSessions(): Promise<{ hostProfile: "cloud" | "local_event"; sessions: SessionSummary[] }> {
    return request("/sessions");
  },

  snapshot(sessionId: string): Promise<SessionSnapshot> {
    return request(`/session?sessionId=${encodeURIComponent(sessionId)}`);
  },

  /**
   * F-031 — tells the host that a display is showing this session.
   *
   * The host cannot work this out on its own in Cloud Mode: the projector
   * connects to Ably, not to the host, so nothing on the host's side of the
   * wire changes when a display appears. Failures are swallowed by the caller —
   * a missed heartbeat costs a softened warning, never a black projector.
   */
  displayHeartbeat(sessionId: string): Promise<void> {
    return request(`/display-heartbeat?sessionId=${encodeURIComponent(sessionId)}`, {
      method: "POST",
      // An empty object rather than no body at all: a bodyless POST carries no
      // content-type, and that is a difference in how the server's body parser
      // is reached rather than something worth being clever about.
      body: "{}",
    });
  },

  /** §24.4 — preflight before the lobby opens. */
  preflight(sessionId: string): Promise<{
    ok: boolean;
    checks: Array<{
      id: string;
      label: string;
      status: "ok" | "warn" | "fail";
      detail: string;
      remedy?: string;
    }>;
    recommendation: "cloud" | "local_event" | null;
    recommendationReason: string | null;
  }> {
    return request(`/preflight?sessionId=${encodeURIComponent(sessionId)}`);
  },

  /**
   * F-074 — measures how fast this device can actually pull from the host.
   *
   * Reachability and latency do not predict how long sixty phones will take to
   * fetch the app, which is what decides the mode at a bandwidth-capped venue.
   * Returns kilobits per second, or null if the probe could not complete.
   */
  async preflightThroughput(sessionId: string): Promise<number | null> {
    try {
      const startedAt = performance.now();
      const response = await fetch(
        `/api/preflight/probe?sessionId=${encodeURIComponent(sessionId)}`,
        {
          credentials: "same-origin",
          cache: "no-store",
          // Ask the host not to compress: a compressed measurement of random
          // bytes would be meaningless, and this saves it the work.
          headers: { ...authHeaders(), "x-no-compression": "1" },
        },
      );
      if (!response.ok) return null;

      const bytes = (await response.arrayBuffer()).byteLength;
      const elapsedMs = performance.now() - startedAt;
      if (elapsedMs <= 0 || bytes === 0) return null;
      return Math.round((bytes * 8) / elapsedMs);
    } catch {
      return null;
    }
  },

  sessionCommand(
    sessionId: string,
    command:
      | { type: "open_lobby" }
      | { type: "start" }
      | { type: "pause" }
      | { type: "resume" }
      | { type: "end" }
      | { type: "remove_player"; sessionPlayerId: string; reason: string }
      | { type: "rename_player"; profileId: string; displayName: string }
      | { type: "correct_score"; sessionPlayerId: string; delta: number; reason: string }
      | { type: "set_low_bandwidth"; enabled: boolean }
      | { type: "set_manual_beat_advance"; enabled: boolean }
      | { type: "set_sound_enabled"; enabled: boolean }
      | { type: "set_access_requests"; enabled: boolean },
  ): Promise<{ snapshot: SessionSnapshot }> {
    return request("/session-command", {
      method: "POST",
      body: JSON.stringify({ sessionId, command }),
    });
  },

  /**
   * Put a beat of the sample-question walkthrough on the projector, or clear it
   * with a null step.
   *
   * Its own method rather than another arm of `sessionCommand` because it is the
   * one command that changes nothing and therefore answers no snapshot — and a
   * caller that had to ignore a snapshot it was handed would be the first sign
   * of this drifting into being a real state transition.
   */
  showSampleStep(sessionId: string, step: number | null): Promise<{ ok: true }> {
    return request("/session-command", {
      method: "POST",
      body: JSON.stringify({ sessionId, command: { type: "show_sample_step", step } }),
    });
  },

  roundCommand(
    sessionId: string,
    command:
      | {
          type: "open";
          questionId: string;
          readingWindowSeconds?: number | null;
          timeLimitSeconds?: number | null;
        }
      | { type: "start_countdown"; roundId: string }
      | { type: "add_time"; roundId: string; seconds?: number }
      | { type: "close_now"; roundId: string }
      | { type: "cancel"; roundId: string; reason: string }
      | { type: "continue_to_board"; roundId: string }
      | { type: "advance_beat"; roundId: string },
  ): Promise<{ ok: true }> {
    return request("/round-command", {
      method: "POST",
      body: JSON.stringify({ sessionId, command }),
    });
  },

  /** §14.4 — the private view: canonical answer, aliases, keywords (F-043). */
  roundDetail(
    sessionId: string,
    roundId: string,
  ): Promise<{
    prompt: string;
    correctAnswer: string;
    acceptedAnswers: string[];
    requiredKeywords: string[];
    forbiddenKeywords: string[];
    explanation: string | null;
    submitted: number;
    eligible: number;
    typed: number;
    multipleChoice: number;
    status: string;
    closesAt: number | null;
    opensAt: number | null;
  }> {
    return request(
      `/round-detail?sessionId=${encodeURIComponent(sessionId)}&roundId=${encodeURIComponent(roundId)}`,
    );
  },

  /**
   * The facilitator's answer-review queue.
   *
   * One entry per distinct answer, ordered by how many players each ruling
   * settles. Fetched on reconnect; the live queue arrives over realtime.
   */
  answerReviewQueue(roundId: string): Promise<AnswerReviewQueue> {
    return request(`/answer-review?roundId=${encodeURIComponent(roundId)}`);
  },

  /** One ruling, applied to every player who gave that answer. */
  ruleAnswer(
    sessionId: string,
    roundId: string,
    normalizedAnswer: string,
    opinion: "correct" | "partial" | "incorrect",
    reason: string | null = null,
  ): Promise<{ ok: true; queue: AnswerReviewQueue }> {
    return request("/answer-review", {
      method: "POST",
      body: JSON.stringify({
        sessionId,
        roundId,
        command: "rule",
        normalizedAnswer,
        opinion,
        reason,
      }),
    });
  },

  /** End review with answers still unresolved, saying explicitly what happens to them. */
  finishAnswerReview(
    sessionId: string,
    roundId: string,
    remainder: "correct" | "partial" | "incorrect" | "local_estimate",
  ): Promise<{ ok: true; queue: AnswerReviewQueue }> {
    return request("/answer-review", {
      method: "POST",
      body: JSON.stringify({ sessionId, roundId, command: "finish", remainder }),
    });
  },

  /** §14.5 — connection health. */
  health(sessionId: string): Promise<{
    experience: PlayerExperience | null;
    lowBandwidthMode: boolean;
    connected: number;
    reconnecting: number;
    disconnected: number;
    displayConnected: boolean;
    medianLatencyMs: number | null;
    database: { ok: boolean; latencyMs: number; detail?: string };
    realtime: { ok: boolean; name: string; detail: string | null };
    queueDepth: number;
    syncStatus: { synced: number; pending: number; failed: number } | null;
    aiGrading: { enabled: boolean; spentMicros: number; budgetMicros: number; exhausted: boolean };
    cpu: number | null;
    /** Used, in MB. */
    memory: number | null;
    /** The instance's limit, in MB. Absent from a server older than this field. */
    memoryTotalMb?: number | null;
  }> {
    return request(`/health?sessionId=${encodeURIComponent(sessionId)}`);
  },

  /** §14.6 — the completed-session view. */
  results(sessionId: string): Promise<{
    summary: unknown;
    questionAnalytics: unknown[];
  }> {
    return request(`/results?sessionId=${encodeURIComponent(sessionId)}`);
  },

  /** Per-player reports: who played enough to be in a batch run. */
  playerReports(sessionId: string): Promise<{
    minAnswered: number;
    players: PlayerReportRosterEntry[];
  }> {
    return request(`/player-reports?sessionId=${encodeURIComponent(sessionId)}`);
  },

  /** Emails reports with their PDFs; ineligible players come back as errors. */
  emailPlayerReports(
    sessionId: string,
    sessionPlayerIds: string[],
  ): Promise<{ results: Array<{ sessionPlayerId: string; email: string; error: string | null }> }> {
    return request("/player-reports/email", {
      method: "POST",
      body: JSON.stringify({ sessionId, sessionPlayerIds }),
    });
  },

  retrySync(sessionId: string): Promise<{ synced: number; pending: number; failed: number }> {
    return request("/retry-sync", { method: "POST", body: JSON.stringify({ sessionId }) });
  },
};

/** One answer waiting on a facilitator, and how many players it settles. */
export interface AnswerReviewItem {
  /** The Answer Map key a ruling is recorded against. */
  normalizedAnswer: string;
  /** What the facilitator reads: the answer as somebody actually typed it. */
  displayAnswer: string;
  playerCount: number;
  opinion: "correct" | "partial" | "incorrect" | "unknown";
  confidence: number;
  reason: string | null;
  matchedConcepts: string[];
  missingConcepts: string[];
}

export interface AnswerReviewQueue {
  items: AnswerReviewItem[];
  playersAffected: number;
  answersAffected: number;
}

export function exportUrl(
  sessionId: string,
  format: "summary-csv" | "responses-csv" | "json",
  includeRawAnswers = false,
): string {
  return `/api/export?sessionId=${encodeURIComponent(sessionId)}&format=${format}&raw=${includeRawAnswers ? "1" : "0"}`;
}

export interface PlayerReportRosterEntry {
  sessionPlayerId: string;
  displayName: string;
  organization: string | null;
  email: string;
  questionsAnswered: number;
  correct: number;
  totalScore: number;
  rank: number | null;
  eligible: boolean;
}

/** One player's report as a PDF download. */
export function playerReportUrl(sessionId: string, sessionPlayerId: string): string {
  return `/api/player-report?sessionId=${encodeURIComponent(sessionId)}&sessionPlayerId=${encodeURIComponent(sessionPlayerId)}`;
}

/** Every eligible player's report, one PDF each, in a ZIP. */
export function playerReportsZipUrl(sessionId: string): string {
  return `/api/player-reports/zip?sessionId=${encodeURIComponent(sessionId)}`;
}
